import { z } from "zod";
import { CatalogSourceStatusSchema, CatalogSupplySourceSchema, PointVectorSchema } from "./catalog.js";
import { EdgeFeatureKindSchema, EdgeInputPolicySchema, EdgeProvisionUnitSchema } from "./requirements.js";
import { IdSchema, IsoTimestampSchema, NonEmptyStringSchema, NonNegativeIntegerSchema, PositiveIntegerSchema, requireSourceOrRuleRef, RuleTraceRefSchema, SourceRefSchema } from "./source.js";
import { RiskTagsSchema } from "./tags.js";
export const RiskTypeSchema = z.enum([
    "io_capacity",
    "catalog_gap",
    "requirement_conflict",
    "mapping_risk",
    "scope_gap",
    "cost_note",
    "rfi",
    "benchmark_mismatch",
    "needsUserDecision",
    "export_blocker",
    "other"
]);
export const RiskSeveritySchema = z.enum(["info", "warning", "blocker"]);
export const RiskScopeSchema = z
    .object({
    level: z.enum([
        "project",
        "apartment_type",
        "controller",
        "device",
        "mapping",
        "requirement",
        "export"
    ]),
    refId: IdSchema.optional(),
    label: NonEmptyStringSchema.optional()
})
    .strict();
export const RiskActionSchema = z
    .object({
    action: NonEmptyStringSchema,
    owner: z.enum(["user", "estimator", "engine", "client", "installer"]),
    status: z.enum(["open", "accepted", "done", "deferred"])
})
    .strict();
export const RiskSchema = requireSourceOrRuleRef(z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    type: RiskTypeSchema,
    tags: RiskTagsSchema,
    severity: RiskSeveritySchema,
    scope: RiskScopeSchema,
    title: NonEmptyStringSchema,
    plainEnglish: NonEmptyStringSchema,
    technicalTrace: z.array(SourceRefSchema),
    recommendedActions: z.array(RiskActionSchema),
    status: z.enum(["open", "accepted", "resolved", "converted_to_rfi"]),
    blocksExport: z.boolean(),
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema),
    createdAt: IsoTimestampSchema
})
    .strict());
export const SpecRiskDispositionSchema = z
    .object({
    level: z.enum(["none", "info", "warning", "blocker"]),
    riskRefs: z.array(IdSchema),
    notes: NonEmptyStringSchema
})
    .strict();
const SpecItemBaseFields = {
    why: NonEmptyStringSchema,
    how: NonEmptyStringSchema,
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema),
    risk: SpecRiskDispositionSchema
};
export const SpecItemContextSchema = requireSourceOrRuleRef(z.object(SpecItemBaseFields).strict());
export const ValueEngineeringMetadataSchema = z
    .object({
    candidate: z.boolean(),
    defaultIncluded: z.boolean(),
    reason: NonEmptyStringSchema
})
    .strict();
export const ValidationFindingSchema = z
    .object({
    code: NonEmptyStringSchema,
    message: NonEmptyStringSchema,
    severity: z.enum(["info", "warning", "error"]),
    subjectRef: IdSchema.optional(),
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema)
})
    .strict();
export const ValidationResultSchema = requireSourceOrRuleRef(z
    .object({
    id: IdSchema,
    status: z.enum(["pending", "accepted", "accepted_with_risks", "rejected"]),
    summary: NonEmptyStringSchema,
    findings: z.array(ValidationFindingSchema),
    checkedAt: IsoTimestampSchema,
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema)
})
    .strict());
export const JustificationSchema = requireSourceOrRuleRef(z
    .object({
    id: IdSchema,
    targetRef: IdSchema,
    why: NonEmptyStringSchema,
    how: NonEmptyStringSchema,
    assumptions: z.array(NonEmptyStringSchema),
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema),
    createdAt: IsoTimestampSchema
})
    .strict());
export const StrategyContextSchema = requireSourceOrRuleRef(z
    .object({
    id: IdSchema,
    projectId: IdSchema,
    apartmentTypeId: IdSchema.optional(),
    catalogReleaseId: IdSchema,
    ruleSetId: IdSchema,
    requirementBaselineId: IdSchema,
    tenderTier: z.enum(["quote", "firm_price", "verified_design"]).optional(),
    assumptions: z.array(NonEmptyStringSchema),
    openDecisionRiskIds: z.array(IdSchema),
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema)
})
    .strict());
export const LogicalZoneSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    apartmentTypeId: IdSchema,
    label: NonEmptyStringSchema,
    roomName: NonEmptyStringSchema.optional(),
    zoneKind: z.enum([
        "fcu",
        "wufh",
        "eufh",
        "heat_only",
        "towel_rail",
        "metering",
        "other"
    ]),
    loadRefs: z.array(IdSchema),
    controlZoneRef: IdSchema.optional()
})
    .strict());
export const ThermalLoadSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    logicalZoneId: IdSchema,
    loadKind: z.enum([
        "fcu",
        "wufh",
        "eufh",
        "heat_only",
        "towel_rail",
        "hiu",
        "ciu",
        "altherma",
        "metering",
        "other"
    ]),
    quantity: PositiveIntegerSchema,
    pointProfileKey: NonEmptyStringSchema.optional()
})
    .strict());
export const PointDemandSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    ownerRef: IdSchema,
    demandKind: NonEmptyStringSchema,
    points: PointVectorSchema,
    consumesHardIo: z.boolean(),
    activeState: z.enum(["active", "spare", "not_terminated"])
})
    .strict());
export const ZoneSlotAssignmentSchema = z
    .object({
    slot: PositiveIntegerSchema,
    logicalZoneId: IdSchema,
    role: z.enum(["primary", "secondary", "overflow"]),
    notes: NonEmptyStringSchema.optional()
})
    .strict();
export const PointViolationSchema = z
    .object({
    pointKind: z.enum(["ui", "di", "ao", "do", "relay"]),
    required: NonNegativeIntegerSchema,
    available: NonNegativeIntegerSchema,
    message: NonEmptyStringSchema
})
    .strict();
export const PointLedgerSuggestionSchema = requireSourceOrRuleRef(z
    .object({
    id: IdSchema,
    suggestionKind: z.enum(["relay_as_do"]),
    severity: z.enum(["info", "warning"]),
    ownerRef: IdSchema,
    pointKind: z.enum(["do"]),
    message: NonEmptyStringSchema,
    recommendedAction: NonEmptyStringSchema,
    riskRef: IdSchema,
    sourceRefs: z.array(SourceRefSchema),
    ruleTraceRefs: z.array(RuleTraceRefSchema)
})
    .strict());
export const PointLedgerEntrySchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    ownerRef: IdSchema,
    pointKind: z.enum(["ui", "di", "ao", "do", "relay"]),
    quantity: NonNegativeIntegerSchema,
    consumesHardIo: z.boolean(),
    activeState: z.enum(["active", "spare", "not_terminated"])
})
    .strict());
export const PointLedgerSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    required: PointVectorSchema,
    available: PointVectorSchema,
    reserved: PointVectorSchema,
    spare: PointVectorSchema,
    violations: z.array(PointViolationSchema),
    suggestions: z.array(PointLedgerSuggestionSchema).optional(),
    entries: z.array(PointLedgerEntrySchema)
})
    .strict());
export const ControllerAssignmentSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    productPartNo: NonEmptyStringSchema,
    role: z.enum([
        "fcu_primary",
        "heat_only_710",
        "mz_overflow",
        "io_module",
        "netmaster",
        "ui",
        "power",
        "accessory"
    ]),
    physicalLocation: z.enum(["fcu_mounted", "central_panel", "room", "unknown"]),
    zoneSlots: z.array(ZoneSlotAssignmentSchema),
    pointLedger: PointLedgerSchema,
    capacity: PointVectorSchema,
    sourceRequirementIds: z.array(IdSchema),
    status: z.enum(["draft", "accepted", "needs_review"])
})
    .strict());
export const PhysicalMappingSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    logicalOwnerRef: IdSchema,
    physicalTerminationRef: IdSchema.optional(),
    roomId: IdSchema.optional(),
    zoneId: IdSchema.optional(),
    mappingKind: z.enum([
        "logical_only",
        "physical_termination",
        "central_io",
        "network_pairing",
        "not_terminated_spare"
    ]),
    cableImplication: NonEmptyStringSchema.optional(),
    commissioningNote: NonEmptyStringSchema
})
    .strict());
export const DeviceMappingSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    deviceRef: IdSchema,
    logicalControllerRef: IdSchema.optional(),
    physicalTerminationRef: IdSchema.optional(),
    roomId: IdSchema.optional(),
    zoneId: IdSchema.optional(),
    mappingType: z.enum([
        "direct_wired",
        "network_pairing",
        "netmaster_point_mapping",
        "central_io",
        "not_terminated_spare"
    ]),
    cableImplication: NonEmptyStringSchema.optional(),
    commissioningNote: NonEmptyStringSchema
})
    .strict());
export const DerivedDeviceSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    productPartNo: NonEmptyStringSchema,
    family: NonEmptyStringSchema,
    role: NonEmptyStringSchema,
    quantity: PositiveIntegerSchema,
    provisionScope: z.enum(["apartment_type", "project"]).optional(),
    ownerRef: IdSchema.optional(),
    pointDemandRefs: z.array(IdSchema),
    valueEngineering: ValueEngineeringMetadataSchema.optional()
})
    .strict());
export const EdgeFeatureSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    featureKind: EdgeFeatureKindSchema,
    provisionUnit: EdgeProvisionUnitSchema,
    inputPolicy: EdgeInputPolicySchema,
    sourceRequirementIds: z.array(IdSchema),
    connectivity: NonEmptyStringSchema.optional(),
    installNotes: z.array(NonEmptyStringSchema),
    needsUserDecision: z.boolean()
})
    .strict());
export const MaterialFieldBasisSchema = z.enum([
    "tender_evidence",
    "catalog_scope_evidence",
    "policy_default"
]);
export const InfrastructureItemSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    id: IdSchema,
    itemKind: z.enum([
        "netmaster",
        "panel",
        "enclosure",
        "power_supply",
        "switch",
        "remote_io_panel",
        "gateway",
        "edge_compute",
        "network_switch",
        "edge_enclosure",
        "cloud_service",
        "metering_unit",
        "relay",
        "io_module",
        "cabling_note",
        "other"
    ]),
    supplySource: CatalogSupplySourceSchema,
    installIncluded: z.boolean(),
    installIncludedBasis: MaterialFieldBasisSchema.optional(),
    quantity: PositiveIntegerSchema,
    productPartNo: NonEmptyStringSchema.optional(),
    protocols: z.array(NonEmptyStringSchema).optional(),
    connectivity: NonEmptyStringSchema.optional(),
    provisionScope: z.enum(["apartment_type", "project"]).optional(),
    provisionScopeBasis: MaterialFieldBasisSchema.optional(),
    commercialModel: z
        .enum(["hardware", "service_subscription", "install_note"])
        .optional(),
    billingPeriod: z.enum(["once", "monthly", "annual"]).optional(),
    installNotes: z.array(NonEmptyStringSchema).optional(),
    alternativeProductPartNos: z.array(NonEmptyStringSchema).optional(),
    selectionStatus: z
        .enum(["selected", "covered_by_existing", "placeholder"])
        .optional(),
    capabilityRole: NonEmptyStringSchema.optional(),
    coveredByInfrastructureItemId: IdSchema.optional(),
    placeholderName: NonEmptyStringSchema.optional(),
    placeholderDescription: NonEmptyStringSchema.optional(),
    sourceStatus: CatalogSourceStatusSchema.optional()
})
    .strict());
export const ApartmentSolutionSchema = requireSourceOrRuleRef(z
    .object({
    ...SpecItemBaseFields,
    apartmentTypeId: IdSchema,
    quantity: PositiveIntegerSchema,
    strategyContextId: IdSchema,
    logicalZones: z.array(LogicalZoneSchema),
    thermalLoads: z.array(ThermalLoadSchema),
    pointDemands: z.array(PointDemandSchema),
    controllers: z.array(ControllerAssignmentSchema),
    devices: z.array(DerivedDeviceSchema),
    edgeFeatures: z.array(EdgeFeatureSchema).optional(),
    infrastructure: z.array(InfrastructureItemSchema),
    physicalMappings: z.array(PhysicalMappingSchema),
    mappings: z.array(DeviceMappingSchema),
    pointLedger: PointLedgerSchema,
    risks: z.array(RiskSchema),
    justifications: z.array(JustificationSchema),
    appliedPolicyFlagRefs: z.array(IdSchema).optional(),
    validation: ValidationResultSchema
})
    .strict());
export const SpecTotalsSchema = z
    .object({
    apartmentCount: NonNegativeIntegerSchema,
    productQuantities: z.record(NonEmptyStringSchema, NonNegativeIntegerSchema),
    pointTotals: PointVectorSchema,
    riskCount: NonNegativeIntegerSchema
})
    .strict();
export const SpecSnapshotSchema = z
    .object({
    id: IdSchema,
    orgId: IdSchema,
    projectId: IdSchema,
    version: NonEmptyStringSchema,
    catalogReleaseId: IdSchema,
    ruleSetId: IdSchema,
    engineVersion: NonEmptyStringSchema,
    status: z.enum(["draft", "accepted", "rejected", "superseded"]),
    apartmentSolutions: z.array(ApartmentSolutionSchema),
    totals: SpecTotalsSchema,
    validation: ValidationResultSchema,
    sourceRequirementIds: z.array(IdSchema),
    sourceOverrideIds: z.array(IdSchema),
    appliedPolicyFlagRefs: z.array(IdSchema).optional(),
    createdAt: IsoTimestampSchema,
    createdByJobId: IdSchema
})
    .strict();
export const SpecRunSchema = z
    .object({
    id: IdSchema,
    orgId: IdSchema,
    projectId: IdSchema,
    version: NonEmptyStringSchema,
    catalogReleaseId: IdSchema,
    ruleSetId: IdSchema,
    status: z.enum(["queued", "running", "accepted", "failed", "cancelled"]),
    inputHash: NonEmptyStringSchema,
    outputSnapshotId: IdSchema.optional(),
    validation: ValidationResultSchema.optional(),
    snapshot: SpecSnapshotSchema,
    solutionHash: z.string(),
    startedAt: IsoTimestampSchema,
    completedAt: IsoTimestampSchema.optional(),
    createdByJobId: IdSchema
})
    .strict();
//# sourceMappingURL=spec.js.map